"use client";

import { useEffect, useRef } from "react";
import type { EstaidEvent, EventProps } from "./events";
import { track } from "./track";

interface TrackOnMountProps<N extends EstaidEvent["name"]> {
    name: N;
    props: EventProps<N>;
    /** The event fires once per distinct key — e.g. the BFE, so opening another property counts again. */
    onceKey: string;
}

/**
 * Drop-in for server route pages that need a "page opened" event: renders
 * nothing and fires `track()` once per `onceKey`. Re-renders and StrictMode's
 * double effect run are deduped through the ref, mirroring `usePageViewTracking`.
 */
export function TrackOnMount<N extends EstaidEvent["name"]>({ name, props, onceKey }: TrackOnMountProps<N>): null {
    const lastKey = useRef<string | null>(null);

    useEffect(() => {
        if (lastKey.current === onceKey) return;
        lastKey.current = onceKey;
        track(name, props);
        // `props` is a fresh object literal on every render; the key decides when to fire.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name, onceKey]);

    return null;
}
